import { useCallback, useEffect, useState } from 'react';

/**
 * Fires `onIntersect` whenever the returned ref'd element scrolls into view.
 * Used as the bottom sentinel for infinite-scroll lists.
 */
export const useIntersection = <T extends HTMLElement>(
  onIntersect: () => void,
  options?: IntersectionObserverInit
) => {
  const [node, setNode] = useState<T | null>(null);
  const ref = useCallback((element: T | null) => {
    setNode(element);
  }, []);

  useEffect(() => {
    if (!node || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onIntersect();
      },
      { rootMargin: '200px', ...options }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [node, onIntersect, options]);

  return ref;
};
